import type { RouterStore } from '../../../../common/mobx-react-router';
import { CurrentUserState } from '../../../../mainv2/states/CurrentUserState';
import { CreditCardState } from '../../../components/molecules/payment/states/CreditCardFormState';
import {
  ApplicationPage,
  ApplicationPageKey,
  MULTICREATOR_APPLICATION_PAGE_KEY,
} from '../../../types/applicationPages';
import { action, computed, observable, makeObservable } from 'mobx';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { ApplicationPageApis } from '../../../apis/applicationPages';
import { EnhancedLocationDescriptor, V3Routes } from '../../../routes';
import { UserFormState } from './UserFormState';
import { SheRavenClient } from '../../../../commonv2/modules/ravenClient';
import moment from 'moment-timezone';
import { CouponState } from './CouponState';
import { BaseListState } from '../../../../commonv3/states/LocationListState';
import { ServiceAgreementKey, ServiceAgreementState } from './../../../states/ServiceAgreementState';
import { ISODate } from '../../../../commonv3/types/dates';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { BaseApplicationPageState } from './BaseApplicationPageState';
import { MulticreatorPackageIndexView, MulticreatorPackagePlanIndexView } from '../../../types/multicreator_packages';
import { MulticreatorPackageApis } from '../../../apis/multicreator_packages';
import { MulticreatorPackageId, MulticreatorPackagePlanId } from '../../../../commonv3/types/multicreator_packages';
import { MulticreatorApplicationApis } from '../../../apis/multicreatorApplications';
import { SessionIndexView, SessionType } from '../../../types/sessions';
import { SessionApis } from '../../../apis/sessions';
import { SessionId } from '../../../../commonv3/types/sessions';
import { SessionPredicates } from '../../../../commonv3/utils/predicates';
import { nextWeekday } from '../../../../commonv3/utils/time';

const MULTICREATOR_DURATION_MONTH = 4;
/**
 * セッションの絞り込み条件に関する定数
 * - 中間講評会: 8,9週目
 * - 最終講評会: 17週目
 * - 1on1キャリアコーチング: 1,2週目
 */
const INTERMEDIATE_REVIEW_START_WEEK = 7;
const INTERMEDIATE_REVIEW_END_WEEK = 9;
const FINAL_REVIEW_START_WEEK = 16;
const FINAL_REVIEW_END_WEEK = 17;
const INITIAL_CAREER_COACHING_START_WEEK = 0;
const INITIAL_CAREER_COACHING_END_WEEK = 2;

export class MulticreatorApplicationPageState implements BaseApplicationPageState {
  userState: CurrentUserState;
  routerState: RouterStore;
  cardState: CreditCardState;
  page_key: ApplicationPageKey = MULTICREATOR_APPLICATION_PAGE_KEY;
  name = null;
  userFormState: UserFormState = new UserFormState();
  baseListState: BaseListState = new BaseListState();
  serviceAgreementState: ServiceAgreementState = new ServiceAgreementState();
  applicationPageResource: RemoteResourceV3<ApplicationPage> = RemoteResourceV3.loading();
  packageListResource: RemoteResourceV3<MulticreatorPackageIndexView[]> = RemoteResourceV3.loading();
  intermediateReviewListResource: RemoteResourceV3<SessionIndexView[]> = RemoteResourceV3.loading();
  finalReviewListResource: RemoteResourceV3<SessionIndexView[]> = RemoteResourceV3.loading();
  initialCoachingListResource: RemoteResourceV3<SessionIndexView[]> = RemoteResourceV3.loading();
  selectedPackagePlanId = null;
  selectedPackageId = null;
  selectedInitialCoachingSessionId = null;
  selectedIntermediateReviewSessionId = null;
  selectedFinalReviewSessionId = null;
  couponState = new CouponState();
  submitState = new SubmitState();
  initState = new SubmitState();
  startDate = null;
  error = null;
  startedFrom: EnhancedLocationDescriptor | null = null;
  entranceInstallments = 1;

  constructor(userState: CurrentUserState, routerState: RouterStore) {
    makeObservable(this, {
      applicationPageResource: observable,
      packageListResource: observable,
      intermediateReviewListResource: observable,
      finalReviewListResource: observable,
      initialCoachingListResource: observable,
      selectedPackagePlanId: observable,
      selectedPackageId: observable,
      selectedInitialCoachingSessionId: observable,
      selectedIntermediateReviewSessionId: observable,
      selectedFinalReviewSessionId: observable,
      couponState: observable,
      submitState: observable,
      initState: observable,
      startDate: observable,
      error: observable,
      startedFrom: observable,
      entranceInstallments: observable,
      reloadSessionList: action.bound,
      selectedPackage: computed,
      selectableIntermediateReviewSessions: computed,
      selectableFinalReviewSessions: computed,
      sessionFilterBaseDate: computed,
      selectableInitialCoachingSessions: computed,
      reloadPackageList: action.bound,
      packageAndSessionsSelected: computed,
      selectableStartDates: computed,
      selectablePackages: computed,
      reloadApplicationPage: action.bound,
      applicationPage: computed,
      totalPrice: computed,
      canGoBack: computed,
      userEmail: computed,
      membershipPlanId: computed,
      setEntranceInstallments: action.bound,
      setStartDate: action.bound,
      selectablePackagePlans: computed,
      setSelectedPackageId: action.bound,
      setSelectedPackagePlanId: action.bound,
      setSelectedFinalReviewSessionId: action.bound,
      setSelectedIntermediateReviewSessionId: action.bound,
      setSelectedInitialCoachingSessionId: action.bound,
      validateStartDateAndNext: action.bound,
      validatePackageAndNext: action.bound,
      validateUserAndNext: action.bound,
      validateCardAndNext: action.bound,
      validateCouponCode: action.bound,
      validateFeeSummaryAndNext: action.bound,
      clearCoupons: action.bound,
      entranceFeeItemName: computed,
      submitting: computed,
      submit: action.bound,
      complete: action.bound,
      completeWithError: action.bound,
      restart: action.bound,
    });

    this.userState = userState;
    this.routerState = routerState;
    this.cardState = new CreditCardState(userState);
  }

  async init() {
    this.page_key = MULTICREATOR_APPLICATION_PAGE_KEY;
    await this.initState.operate(async () => {
      return await Promise.all([
        this.reloadPackageList(),
        this.reloadApplicationPage(),
        this.reloadSessionList(),
        this.userState.reloadUser(),
        this.cardState.init(),
        this.baseListState.init(),
      ]);
    });

    if (this.expiredMulticreatorApplyPage()) {
      return GlobalStatesV3.routing.push(V3Routes.multicreatorFull());
    }

    if (this.applicationPageResource.isNotFound() || this.applicationPageResource.isOperationalError()) {
      window.location.replace('/');
    } else if (this.applicationPage && !this.applicationPage.qualified) {
      window.location.replace('/');
    } else {
      if (!this.userState.isSignedIn()) {
        this.userFormState.init();
        this.startedFrom = V3Routes.multicreatorApplicationUserInfo();
      } else {
        this.userFormState.init(this.userState.currentUser);
        this.startedFrom = V3Routes.multicreatorApplicationUserSummary();
      }
      this.serviceAgreementState.init(this.applicationPage.service_agreement_key || ServiceAgreementKey.STANDARD);
      this.routerState.replace(this.startedFrom);
    }
  }

  async reloadSessionList() {
    await Promise.all([
      updateRemoteResourceV3(
        this.intermediateReviewListResource,
        (v) => (this.intermediateReviewListResource = v),
        () =>
          SessionApis.findAll({
            session_type: SessionType.MULTICREATOR_INTERMEDIATE_REVIEW,
            only_reservable: 'true',
          })
      ),
      updateRemoteResourceV3(
        this.finalReviewListResource,
        (v) => (this.finalReviewListResource = v),
        () =>
          SessionApis.findAll({
            session_type: SessionType.MULTICREATOR_FINAL_REVIEW,
            only_reservable: 'true',
          })
      ),
      updateRemoteResourceV3(
        this.initialCoachingListResource,
        (v) => (this.initialCoachingListResource = v),
        () =>
          SessionApis.findAll({
            session_type: SessionType.INITIAL_MULTICREATOR_CAREER_COACHING,
            only_reservable: 'true',
          })
      ),
    ]);
  }

  get selectedPackage(): MulticreatorPackageIndexView | null {
    if (!this.selectedPackageId) {
      return null;
    }
    return this.packageListResource.getOrElse([]).find((p) => p.id === this.selectedPackageId);
  }

  /**
   * 選択可能な中間講評会の一覧を返す
   * パッケージの受講開始日の次の月曜日を起点として、第8週目に開催される枠のみを返す
   * @see SHE-2652
   */
  get selectableIntermediateReviewSessions(): SessionIndexView[] {
    if (!this.selectedPackage) {
      return [];
    }
    return this.intermediateReviewListResource
      .getOrElse([])
      .filter(
        SessionPredicates.startsInWeeks(
          this.sessionFilterBaseDate,
          INTERMEDIATE_REVIEW_START_WEEK,
          INTERMEDIATE_REVIEW_END_WEEK
        )
      );
  }

  /**
   * 選択可能な最終講評会の一覧を返す
   * パッケージの受講開始日の次の月曜日を起点として、第17週目に開催される枠のみを返す
   * @see SHE-2652
   */
  get selectableFinalReviewSessions(): SessionIndexView[] {
    if (!this.selectedPackage) {
      return [];
    }
    // 次の月曜日を起点とする
    return this.finalReviewListResource
      .getOrElse([])
      .filter(
        SessionPredicates.startsInWeeks(this.sessionFilterBaseDate, FINAL_REVIEW_START_WEEK, FINAL_REVIEW_END_WEEK)
      )
      .filter(
        SessionPredicates.startsBefore(
          moment(this.selectedPackage.start_date).add(MULTICREATOR_DURATION_MONTH, 'months')
        )
      );
  }

  /**
   * 各セッション（最終講評会・中間講評会・初回キャリアコーチング）の絞り込みの起点となる日を返す。
   * 現在は、受講開始日の次の月曜日
   */
  get sessionFilterBaseDate() {
    return nextWeekday(this.selectedPackage.start_date, 1);
  }

  /**
   * 選択可能な初回キャリアコーチングの一覧を返す
   * パッケージの受講開始日を起点として、第1,2週目に開催される枠のみを返す
   * @see SHE-2652
   * @see SHE-4680
   */
  get selectableInitialCoachingSessions(): SessionIndexView[] {
    if (!this.selectedPackage) {
      return [];
    }
    return this.initialCoachingListResource
      .getOrElse([])
      .filter(
        SessionPredicates.startsInWeeks(
          this.selectedPackage.start_date,
          INITIAL_CAREER_COACHING_START_WEEK,
          INITIAL_CAREER_COACHING_END_WEEK
        )
      );
  }

  async reloadPackageList() {
    await updateRemoteResourceV3(
      this.packageListResource,
      (v) => (this.packageListResource = v),
      () => MulticreatorPackageApis.index()
    );
  }

  get packageAndSessionsSelected(): boolean {
    return (
      this.selectedPackageId != null &&
      this.selectedIntermediateReviewSessionId != null &&
      this.selectedFinalReviewSessionId != null &&
      this.selectedInitialCoachingSessionId != null
    );
  }

  get selectableStartDates(): ISODate[] {
    return [...new Set(this.packageListResource.getOrElse([]).map((p) => p.start_date))];
  }

  get selectablePackages(): MulticreatorPackageIndexView[] {
    if (!this.startDate) {
      return [];
    }
    return this.packageListResource
      .getOrElse([])
      .filter((p) => moment(p.start_date).isSame(moment(this.startDate)))
      .filter((p) => p.multicreator_package_plan.id === this.selectedPackagePlanId);
  }

  async reloadApplicationPage() {
    try {
      await updateRemoteResourceV3(
        this.applicationPageResource,
        (v) => (this.applicationPageResource = v),
        () => ApplicationPageApis.show(this.page_key, this.userEmail, this.couponState.appliedCouponCode)
      );
      this.name = this.applicationPage.name;
    } catch (error) {
      this.routerState.replace(V3Routes.home());
    }
  }

  get applicationPage(): ApplicationPage {
    return this.applicationPageResource.get();
  }

  get totalPrice() {
    const entranceFee = this.applicationPage.entrance_fee_item;
    let price = this.applicationPage.membership_item.total_price;
    if (entranceFee) {
      const option = entranceFee.options.find((o) => o.installments === this.entranceInstallments);
      price += option.monthly_payments[0];
    }
    return price;
  }

  get canGoBack(): boolean {
    if (!this.startedFrom) {
      return false;
    }
    return this.startedFrom.pathname !== this.routerState.location.pathname;
  }

  get userEmail() {
    if (this.userState.isSignedIn()) {
      return this.userState.currentUser.email;
    }
    return this.userFormState.fields.email.getDirtyValue();
  }

  get membershipPlanId() {
    return this.applicationPage.membership_item.membership_plan_id;
  }

  async setEntranceInstallments(installments: number) {
    this.entranceInstallments = installments;
  }

  setStartDate(date: moment.MomentInput) {
    this.startDate = moment(date);
    if (this.selectablePackagePlans.length > 0) {
      this.selectedPackagePlanId = this.selectablePackagePlans[0].id;
    }
  }

  get selectablePackagePlans(): MulticreatorPackagePlanIndexView[] {
    const packages: MulticreatorPackageIndexView[] = this.packageListResource
      .getOrElse([])
      .filter((p) => moment(p.start_date).isSame(moment(this.startDate)));
    // ユニークなパッケージの一覧を取得する
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const distinctPackages: any[] = Object.values(
      packages.reduce((dict, p) => {
        dict[p.multicreator_package_plan.id] = p.multicreator_package_plan;
        return dict;
      }, {})
    );
    // positionによってソートして返す
    return distinctPackages.sort(
      (x: MulticreatorPackagePlanIndexView, y: MulticreatorPackagePlanIndexView) => x.position - y.position
    );
  }

  setSelectedPackageId(packageId: MulticreatorPackageId | null) {
    this.selectedPackageId = packageId;
    // パッケージが変更された場合、中間講評会・最終講評会・初回キャリアコーチングの選択状態もリセットされるべき
    this.selectedIntermediateReviewSessionId = null;
    this.selectedFinalReviewSessionId = null;
    this.selectedInitialCoachingSessionId = null;
  }

  setSelectedPackagePlanId(packagePlanId: MulticreatorPackagePlanId) {
    this.selectedPackagePlanId = packagePlanId;
    // プランが変更された場合は選択を解除する
    this.setSelectedPackageId(null);
  }

  setSelectedFinalReviewSessionId(sessionId: SessionId) {
    this.selectedFinalReviewSessionId = sessionId;
  }

  setSelectedIntermediateReviewSessionId(sessionId: SessionId) {
    this.selectedIntermediateReviewSessionId = sessionId;
  }

  setSelectedInitialCoachingSessionId(sessionId: SessionId) {
    this.selectedInitialCoachingSessionId = sessionId;
  }

  async validateStartDateAndNext() {
    if (!this.startDate) {
      return;
    }
    this.routerState.push(V3Routes.multicreatorApplicationPackageSelect());
  }

  async validatePackageAndNext() {
    this.routerState.push(V3Routes.multicreatorApplicationSummary());
  }

  async validateUserAndNext() {
    await this.submitState.operate(async () => {
      await this.userFormState.validate();
      if (this.userFormState.hasErrors()) {
        return;
      }
      this.routerState.push(V3Routes.multicreatorApplicationStartDateSelect());
    });
  }

  hasSingleFixedEffectiveDate(fixed_effective_dates: Array<ISODate>): boolean {
    if (fixed_effective_dates.length == 1) {
      return true;
    } else {
      return false;
    }
  }

  expiredMulticreatorApplyPage(): boolean {
    if (
      this.page_key == MULTICREATOR_APPLICATION_PAGE_KEY &&
      this.applicationPageResource.isError() &&
      this.applicationPageResource.error.body == '有効期限が切れています'
    ) {
      return true;
    } else {
      return false;
    }
  }

  async validateCardAndNext() {
    await this.submitState.operate(async () => {
      if (this.cardState.editMode) {
        if (!(await this.cardState.validateNewCard())) {
          return;
        }
      }
      this.error = null;
      this.routerState.push(V3Routes.multicreatorApplicationConfirmation());
    });
  }

  async validateCouponCode() {
    await this.submitState.operate(async () => {
      await this.couponState.submit(this.userEmail, this.membershipPlanId);
      if (this.couponState.appliedCouponCode) {
        await Promise.all([this.reloadApplicationPage()]);
      }
    });
  }

  async validateFeeSummaryAndNext() {
    return GlobalStatesV3.routing.push(V3Routes.multicreatorApplicationPaymentMethod());
  }

  async clearCoupons() {
    await this.submitState.operate(async () => {
      this.couponState.clearCoupons();
      await Promise.all([this.reloadApplicationPage()]);
    });
  }

  get entranceFeeItemName(): string {
    return this.applicationPage.membership_item.price > 0 ? '入会金' : this.applicationPage.entrance_fee_item.name;
  }

  get submitting(): boolean {
    return this.submitState.submitting;
  }

  async submit() {
    try {
      await this.submitState.operate(async () => {
        this.error = null;
        const response = await MulticreatorApplicationApis.apply({
          user: this.userState.isSignedIn() ? undefined : this.userFormState.getDirtyValue(),
          card: this.cardState.newToken ? { token: this.cardState.newToken } : undefined,
          entrance_installments: this.entranceInstallments,
          coupon_code: this.couponState.appliedCouponCode,
          effective_date: this.startDate.format('YYYY-MM-DD'),
          application_package_id: this.selectedPackageId,
          application_intermediate_review_session_id: this.selectedIntermediateReviewSessionId,
          application_final_review_session_id: this.selectedFinalReviewSessionId,
          application_initial_coaching_session_id: this.selectedInitialCoachingSessionId,
        });
        await this.userState.reloadUser();
        // Handle partial failures of orders
        if (response.orders.find((order) => order.payments.some((o) => o.status === 'error'))) {
          this.error = response.orders;
        }
      });
      this.routerState.push(V3Routes.multicreatorApplicationComplete());
    } catch (e) {
      SheRavenClient.captureException(e, {
        user: this.userState.currentUser,
        applicationPage: this.applicationPage,
      });
      this.error = e;
    }
  }

  complete() {
    // A temporary solution to force refresh the user info
    // API client doesn't track the cookie update
    location.href = V3Routes.home().stringify();
  }

  completeWithError() {
    location.href = V3Routes.payments().stringify();
  }

  restart() {
    location.href = V3Routes.application().stringify();
  }
}
